import { useToast } from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { SelectionSettings } from '../../../../hooks/useGetSettings';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import { SettingsFlow, useSettingsDetailCrumbs } from '../../../../components/settings/primitives';
import {
    fromPriceMatchDraft,
    isPriceMatchDraftValid,
    toPriceMatchDraft,
    type PriceMatchDraftEntry,
} from '../../../../components/settings/priceMatchCriteria';
import PriceMatchOrderEditor from './PriceMatchOrderEditor';

export interface PriceMatchOrderSectionProps {
    data: SelectionSettings;
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

/**
 * The custom price matching order for non-DRP jobs, full screen: how a card is
 * built on the left, the order the cards are tried in on the right. Replaces the
 * ranking modal, so building and reordering the order get the whole window.
 *
 * Saving writes the whole selection subdocument through POST /settings/update,
 * which is how that endpoint works, so the order is merged into the current
 * settings here rather than staged on the Selection section's draft. Choosing a
 * custom order is the same act as having one, so the mode is written with it.
 */
const PriceMatchOrderSection = ({ data, refetch }: PriceMatchOrderSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();

    const [entries, setEntries] = useState<Array<PriceMatchDraftEntry>>(() =>
        Array.isArray(data.nonDrpPriceMatch) ? data.nonDrpPriceMatch.map(toPriceMatchDraft) : [],
    );

    useSettingsDetailCrumbs([{ label: 'Custom price matching order' }]);

    const closeFlow = () => navigate('/settings/selection');

    // A blank card added from the rail but never filled in would match
    // everything, so saving waits until every card has a criterion.
    const hasEmptyCard = entries.some((entry) => !isPriceMatchDraftValid(entry));

    const handleSave = () => {
        if (hasEmptyCard) return;
        updateSettings.mutate(
            {
                selection: {
                    ...data,
                    nonDrpPriceMatch: entries.map(fromPriceMatchDraft),
                    // Mirroring the estimate's part types only applies to
                    // matching against the estimate itself.
                    nonDrpMirrorEstimateTypes: false,
                },
            },
            {
                onSuccess: () => {
                    toast({
                        title: 'Price match order saved',
                        description:
                            entries.length === 0
                                ? 'No cards configured, so we will price match to the estimate list price.'
                                : `${entries.length} card${entries.length === 1 ? '' : 's'} saved.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: 'Could not save price match order',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    return (
        <SettingsFlow
            steps={[{ id: 'order', label: 'Price match order' }]}
            activeStepId="order"
            onCancel={closeFlow}
            onSave={handleSave}
            saveLabel="Save Order"
            isSaveDisabled={hasEmptyCard}
            isSaving={updateSettings.isPending}
        >
            <PriceMatchOrderEditor entries={entries} onChange={setEntries} />
        </SettingsFlow>
    );
};

export default PriceMatchOrderSection;
